/**
 * ResourceBars — molecule (CES standard)
 * Memory / vCPU / GPU usage bars with the v1 color rule:
 * green <80%, yellow 80-99%, red >=100% (slotting-core getBarColorClass).
 * `.bar-fill` + color classes stay in visualizer.css (E2E contract).
 */

import { getBarColorClass } from 'slotting-core';
import '../visualizer.css';

interface BarProps {
  label: string;
  used: number;
  total: number;
  unit?: string;
  testId: string;
}

export function ResourceBar({ label, used, total, unit, testId }: BarProps) {
  const pct = total > 0 ? (used / total) * 100 : 0;
  const color = getBarColorClass(pct);
  const suffix = unit ? ` ${unit}` : '';
  return (
    <div className="grid grid-cols-[64px_1fr_120px] items-center gap-3" data-testid={testId}>
      <span className="text-xs uppercase tracking-wide text-gray-500">{label}</span>
      <div className="h-2 overflow-hidden rounded bg-gray-200">
        <div
          className={`bar-fill ${color}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
          role="progressbar"
          aria-valuenow={Math.round(pct)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${label} usage`}
        />
      </div>
      <span className="text-right text-xs text-gray-500 tabular-nums">
        {used} / {total}
        {suffix}
      </span>
    </div>
  );
}

export function ResourceBars({
  memoryUsed,
  memoryTotal,
  vcpusUsed,
  vcpusTotal,
  gpusUsed,
  gpusTotal,
}: {
  memoryUsed: number;
  memoryTotal: number;
  vcpusUsed: number;
  vcpusTotal: number;
  gpusUsed?: number;
  gpusTotal?: number;
}) {
  const showGpus = gpusTotal !== undefined && gpusTotal > 0;
  return (
    <div className="mt-3 flex flex-col gap-2">
      <ResourceBar
        label="Memory"
        used={memoryUsed}
        total={memoryTotal}
        unit="GiB"
        testId="bar-memory"
      />
      <ResourceBar label="vCPUs" used={vcpusUsed} total={vcpusTotal} testId="bar-vcpus" />
      {showGpus && (
        <ResourceBar label="GPUs" used={gpusUsed ?? 0} total={gpusTotal} testId="bar-gpus" />
      )}
    </div>
  );
}
